import React, { useState, useEffect } from "react";
import {
  Github,
  Linkedin,
  Mail,
  FileText,
  Database,
  BarChart2,
  Code,
  Brain,
  ChevronDown,
  ExternalLink,
  Send,
  Terminal,
  PieChart,
} from "lucide-react";

// --- MOCK DATA ---

const PERSONAL_INFO = {
  name: "Amith D",
  role: "Data Analyst & Insights Specialist",
  tagline: "Translating raw data into actionable business strategies.",
  about:
    "I am a detail-oriented Data Analyst with a passion for uncovering trends and patterns. Fresh out of university with a strong foundation in SQL, Python, and Visualization tools, I build dashboards that tell stories and models that predict the future.",
  email: "amith12096@gmail.com",
  github: "https://github.com",
  resume:
    "https://ppl-ai-file-upload.s3.amazonaws.com/web/direct-files/attachments/57664550/ee5ad834-f43e-4ed4-9a19-0128f0c3d5a0/AMITH-D.pdf", // Add your Google Drive resume URL here
  linkedin: "https://www.linkedin.com/in/amithd024/",
};

const SKILLS = [
  {
    category: "Analysis & Code",
    icon: <Code className="w-6 h-6 text-cyan-400" />,
    items: [
      "Python (Pandas, NumPy)",
      "SQL (PostgreSQL, MySQL)",
      "R",
      "Jupyter Notebooks",
    ],
  },
  {
    category: "Visualization",
    icon: <PieChart className="w-6 h-6 text-purple-400" />,
    items: ["Tableau", "Power BI", "Matplotlib", "Seaborn", "Excel (Advanced)"],
  },
  {
    category: "Data Engineering",
    icon: <Database className="w-6 h-6 text-emerald-400" />,
    items: [
      "ETL Pipelines",
      "Data Cleaning",
      "Warehousing Concepts",
      "Web Scraping",
    ],
  },
  {
    category: "Machine Learning",
    icon: <Brain className="w-6 h-6 text-pink-400" />,
    items: [
      "Regression Models",
      "Clustering",
      "Scikit-learn",
      "Predictive Analytics",
    ],
  },
];

const PROJECTS = [
  {
    id: 1,
    title: "OLIST E-Commerce Analytics Case Study",
    description:
      "CAnalyzed 100K+ transactions across 2016-2018 Brazilian e-commerce dataset. Executed 10 complex SQL business queries uncovering 10x revenue growth to $7.2M, 96.88% customer retention rate, and $1.4M top-performing product category. Built 3-page interactive Power BI dashboard with KPI tracking and actionable insights projecting $500K+ potential revenue gains through logistics optimization.",
    tech: ["SQL", "Power BI", "Data Analysis"],
    color: "from-blue-500",
    github:
      "https://github.com/amith-1296/OLIST-E-Commerce-Analytics-Case-Study",
  },
  {
    id: 2,
    title: "Cookie Cats A/B Testing",
    description:
      "A compConducted rigorous A/B test on 90,118 mobile game users comparing two game versions. Applied statistical analysis (Chi-Square, Bootstrap with 1,000 iterations) achieving 99.8% confidence level. Identified 4.2% retention drop that would cost $950K+ annually, preventing deployment and saving substantial revenue loss.",
    tech: ["Python", "A/B Testing", "Statistics"],
    color: "from-purple-500",
    github: "https://github.com/amith-1296/cookie-cats-ab-testing",
  },
  {
    id: 3,
    title: "Customer Churn Analytics Dashboard",
    description:
      "Interactive Customer Churn Analytics Dashboard with 8 data visualization charts. Built with HTML, CSS, and JavaScript. Features churn analysis by geography, age group, product count, gender, aBuilt interactive Streamlit dashboard analyzing 10,000 customers with 8 data visualizations across 3 professional tabs. Identified critical churn patterns: 82.7% churn rate for 3+ product users, 32.4% churn in Germany (2x higher than France), and 42.5% early-stage customer risk. Delivered 4 KPI metrics enabling targeted retention strategies.",
    tech: ["JavaScript", "HTML/CSS", "Data Visualization"],
    color: "from-emerald-500",
    github: "https://github.com/amith-1296/customer-churn-analytics-dashboard",
  },
  {
    id: 4,
    title: "Retail Sales Analytics Dashboard",
    description:
      "Interactive Retail Sales Analytics Dashboard with Python - Executive BI Dashboard built with Streamlit, Plotly & Pandas. Features comprehensive sales analysis with interactive visualizations andDeveloped executive BI dashboard processing 5,000+ retail transactions with Python, Streamlit & Plotly. Discovered Sydney as top market ($2.67M sales), Corporate segment driving $1.35M revenue, and Office Supplies dominating 78% of sales. Created 9 interactive visualizations with dynamic filtering, KPI cards tracking $5M+ in total revenue and operational metrics.",
    tech: ["Python", "Streamlit", "Plotly"],
    color: "from-cyan-500",
    github: "https://github.com/amith-1296/retail-sales-analytics-dashboard",
  },
  {
    id: 5,
    title: "Social Media Tourism Analytics",
    description:
      "MBA capstone project analyzing social media behavior to predict product purchase propensity in the aviation/tourism industry. Built K-means clustering achieving 99% accuracy for mobile users and 98% for laptop users using Decision Tree, KNN, Random Forest, SVM, and Naive Bayes models with bagging/boosting techniques.",
    tech: ["Python", "Machine Learning", "Scikit-learn", "Data Analysis"],
    color: "from-indigo-500",
    github: "https://github.com/amith-1296/Social-Media-Tourism-Analytics",
  },
];

const TIMELINE = [
  {
    year: "2022 - 2024",
    title: "MBA in Data Science and Analytics",
    institution: "Jain University",
    desc: "Advanced degree focused on data-driven decision making.",
  },
  {
    year: "2020",
    title: "BE in Computer Science",
    institution: "Bangalore University",
    desc: "Comprehensive undergraduate program in computer science and engineering.",
  },
  {
    year: "2023",
    title: "Google Data Analytics Certificate",
    institution: "Coursera",
    desc: "Intensive training in data cleaning, analysis, and visualization.",
  },

  {
    year: "2023",
    title: "DataCamp Certificates in Python and SQL",
    institution: "DataCamp",
    desc: "Professional certifications in Python programming and SQL database querying.",
  },
];

// --- COMPONENTS ---

const SectionTitle = ({
  children,
  subtitle,
}: {
  children: React.ReactNode;
  subtitle?: string;
}) => (
  <div className="mb-12 text-center">
    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 relative inline-block">
      {children}
      <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-full"></span>
    </h2>
    {subtitle && (
      <p className="text-slate-400 mt-4 max-w-2xl mx-auto">{subtitle}</p>
    )}
  </div>
);

export default function Portfolio() {
  const [activeSection, setActiveSection] = useState("home");
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll effects
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setActiveSection(id);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-200 font-sans selection:bg-cyan-500/30 selection:text-cyan-200">
      {/* Background Grid Animation */}
      <div className="fixed inset-0 z-0 opacity-20 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <div className="absolute left-0 right-0 top-0 -z-10 m-auto h-[310px] w-[310px] rounded-full bg-cyan-500 opacity-20 blur-[100px]"></div>
        <div className="absolute right-0 bottom-0 -z-10 m-auto h-[310px] w-[310px] rounded-full bg-purple-500 opacity-20 blur-[100px]"></div>
      </div>

      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-slate-900/80 backdrop-blur-md border-b border-slate-800 py-4"
            : "bg-transparent py-6"
        }`}
      >
        <div className="container mx-auto px-6 flex justify-between items-center">
          <div className="text-xl font-bold tracking-tighter text-white flex items-center gap-2">
            <Terminal className="w-6 h-6 text-cyan-400" />
            <span>
              Amith<span className="text-cyan-400">.DATA</span>
            </span>
          </div>
          <div className="hidden md:flex gap-8 text-sm font-medium">
            {["About", "Skills", "Projects", "Resume"].map((item) => (
              <button
                key={item}
                onClick={() => scrollTo(item.toLowerCase())}
                className="hover:text-cyan-400 transition-colors"
              >
                {item}
              </button>
            ))}
          </div>
          <a
            href={`mailto:${PERSONAL_INFO.email}`}
            className="bg-cyan-500 hover:bg-cyan-600 text-slate-900 font-bold py-2 px-6 rounded-full transition-all transform hover:scale-105"
          >
            Hire Me
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section
        id="home"
        className="relative pt-32 pb-20 md:pt-48 md:pb-32 px-6 z-10 container mx-auto flex flex-col items-center text-center"
      >
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-800 border border-slate-700 text-cyan-400 text-xs font-semibold tracking-wider mb-6 animate-fade-in-up">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
          </span>
          AVAILABLE FOR WORK
        </div>

        <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight leading-tight animate-fade-in-up delay-100">
          Turning Complex Data <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
            Into Clear Decisions
          </span>
        </h1>

        <p className="text-lg md:text-xl text-slate-400 max-w-2xl mb-10 animate-fade-in-up delay-200">
          {PERSONAL_INFO.about}
        </p>

        <div className="flex flex-col md:flex-row gap-4 animate-fade-in-up delay-300">
          <button
            onClick={() => scrollTo("projects")}
            className="flex items-center justify-center gap-2 bg-white text-slate-900 font-bold py-3 px-8 rounded-full hover:bg-slate-200 transition-all"
          >
            <BarChart2 className="w-4 h-4" />
            View Projects
          </button>
          <a
            href={PERSONAL_INFO.github}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 border border-slate-700 text-white font-medium py-3 px-8 rounded-full hover:bg-slate-800 transition-all"
          >
            <Github className="w-4 h-4" />
            GitHub Profile
          </a>
        </div>

        <div className="mt-20 animate-bounce text-slate-500">
          <ChevronDown className="w-6 h-6" />
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-20 bg-slate-800/50 relative z-10">
        <div className="container mx-auto px-6">
          <SectionTitle subtitle="My technical toolkit for solving data problems.">
            Technical Expertise
          </SectionTitle>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SKILLS.map((skill, index) => (
              <div
                key={index}
                className="bg-slate-900 border border-slate-800 p-6 rounded-2xl hover:border-cyan-500/50 transition-all group"
              >
                <div className="w-12 h-12 bg-slate-800 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  {skill.icon}
                </div>
                <h3 className="text-lg font-bold text-white mb-3">
                  {skill.category}
                </h3>
                <ul className="space-y-2">
                  {skill.items.map((item, i) => (
                    <li
                      key={i}
                      className="text-slate-400 text-sm flex items-center gap-2"
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-cyan-500/50"></div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-20 relative z-10">
        <div className="container mx-auto px-6">
          <SectionTitle subtitle="Real-world applications of my skills.">
            Featured Projects
          </SectionTitle>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {PROJECTS.map((project) => (
              <div
                key={project.id}
                className="group relative rounded-2xl overflow-hidden bg-slate-800 border border-slate-700 hover:border-slate-500 transition-all hover:-translate-y-2"
              >
                {/* Gradient Header */}
                <div className={`h-2 bg-gradient-to-r ${project.color}`}></div>

                <div className="p-8">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-bold text-white group-hover:text-cyan-400 transition-colors">
                      {project.title}
                    </h3>
                    <ExternalLink className="w-5 h-5 text-slate-500 group-hover:text-white transition-colors" />
                  </div>

                  <p className="text-slate-400 mb-6 text-sm leading-relaxed">
                    {project.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mt-auto">
                    {project.tech.map((t, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-slate-900 text-cyan-400 text-xs font-medium rounded-full border border-slate-700"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Resume/Timeline Section */}
      <section id="resume" className="py-20 bg-slate-800/50 relative z-10">
        <div className="container mx-auto px-6 max-w-4xl">
          <SectionTitle>Education & Certifications</SectionTitle>

          <div className="space-y-8 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-700 before:to-transparent">
            {TIMELINE.map((item, index) => (
              <div
                key={index}
                className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active"
              >
                {/* Icon */}
                <div className="flex items-center justify-center w-10 h-10 rounded-full border border-slate-700 bg-slate-900 group-hover:border-cyan-500 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10">
                  <div className="w-3 h-3 bg-cyan-500 rounded-full animate-pulse"></div>
                </div>

                {/* Content */}
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-slate-900 p-6 rounded-xl border border-slate-800 shadow-sm hover:border-slate-600 transition-colors">
                  <div className="flex items-center justify-between space-x-2 mb-1">
                    <div className="font-bold text-slate-200">{item.title}</div>
                    <time className="font-mono text-xs text-cyan-400">
                      {item.year}
                    </time>
                  </div>
                  <div className="text-slate-400 text-sm font-medium mb-2">
                    {item.institution}
                  </div>
                  <div className="text-slate-500 text-sm">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <a
              href={PERSONAL_INFO.resume}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white font-medium py-3 px-8 rounded-full transition-all"
            >
              <FileText className="w-4 h-4" />
              Download Full Resume
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 relative z-10">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="bg-gradient-to-br from-cyan-900/20 to-purple-900/20 rounded-3xl p-8 md:p-12 border border-slate-700 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to make data-driven decisions?
            </h2>
            <p className="text-slate-400 mb-8 max-w-xl mx-auto">
              I'm currently looking for full-time Data Analyst opportunities.
              Whether you have a question or just want to say hi, my inbox is
              always open!
            </p>

            <div className="flex flex-col md:flex-row items-center justify-center gap-6">
              <a
                href={`mailto:${PERSONAL_INFO.email}`}
                className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold py-4 px-8 rounded-full hover:shadow-lg hover:shadow-cyan-500/25 transition-all transform hover:-translate-y-1"
              >
                <Mail className="w-5 h-5" />
                Send Me an Email
              </a>
              <div className="flex gap-4">
                <a
                  href={PERSONAL_INFO.linkedin}
                  className="p-4 bg-slate-800 rounded-full hover:bg-blue-600 hover:text-white transition-all text-slate-400"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a
                  href={PERSONAL_INFO.github}
                  className="p-4 bg-slate-800 rounded-full hover:bg-slate-700 hover:text-white transition-all text-slate-400"
                >
                  <Github className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-900 border-t border-slate-800 text-center text-slate-500 text-sm relative z-10">
        <p>
          © {new Date().getFullYear()} Amith D. Built with React & Tailwind.
        </p>
      </footer>

      {/* Custom Styles for Simple Animations */}
      <style>{`
        @keyframes fade-in-up {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }
        .delay-100 { animation-delay: 0.1s; }
        .delay-200 { animation-delay: 0.2s; }
        .delay-300 { animation-delay: 0.3s; }
      `}</style>
    </div>
  );
}
